import { useEffect, useState } from 'react';
import { api } from '../../api-client.js';
import { fmtMoney } from './types.js';

// ---------------------------------------------------------------------------
// Golden Balls player record
// ---------------------------------------------------------------------------
// Summary rates by default; the per-game history list appears only when
// the server says this viewer may see it (always for your own profile,
// otherwise governed by the exposure flag — full history is more
// interesting to read and more exploitable).
//
// Abandoned and incomplete games never reach the performance rates —
// the server keeps them in the abandonment count only.

type Summary = {
  gamesPlayed: number;
  reachedFinal: number;
  jackpotsWon: number;
  splitRate: number | null;
  stealRate: number | null;
  stingRate: number | null;
  timesStolenFrom: number;
  stingsRight: number;
  stingsWrong: number;
  honestyRate: number | null;
  intentMatchRate: number | null;
  averageRoundSurvived: number | null;
  largestJackpot: number;
  totalWon: number;
  timeoutCount: number;
  abandonCount: number;
  tier: number;
};

type HistoryRow = {
  matchId: string;
  ballMode: string;
  isPrivateLobby: boolean;
  reachedFinal: boolean;
  eliminatedRound: number | null;
  finalChoice: string | null;
  jackpot: number;
  payout: number;
  claimsTrue: number;
  claimsLied: number;
  playedAt: string;
};

type RecordsResponse = {
  summary: Summary | null;
  history?: HistoryRow[];
  historyVisible: boolean;
  tierFormula: { text: string };
};

const pct = (rate: number | null): string =>
  rate === null ? '—' : `${Math.round(rate * 100)}%`;

export function RecordsPanel({ userId }: { userId: string }) {
  const [data, setData] = useState<RecordsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getGoldenBallsRecords(userId)
      .then((response) => {
        if (!cancelled) setData(response as RecordsResponse);
      })
      .catch((error) => {
        if (!cancelled) setError(String(error));
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (error) return null;
  if (!data) return null;

  const summary = data.summary;

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-ec-line bg-ec-panel p-3">
      <header className="flex flex-wrap items-baseline gap-2">
        <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-ec-face">
          Golden Balls record
        </h3>
        {summary && summary.tier > 0 && (
          <span
            title={data.tierFormula.text}
            className="rounded-full border border-ec-brass/60 px-1.5 font-display text-[0.55rem] uppercase tracking-wide text-ec-brass"
          >
            tier {summary.tier}
          </span>
        )}
      </header>

      {!summary || summary.gamesPlayed === 0 ? (
        <p className="font-serif text-xs text-ec-bone-dim">No completed games yet.</p>
      ) : (
        <>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 sm:grid-cols-3">
            <Stat label="Games" value={String(summary.gamesPlayed)} />
            <Stat label="Reached final" value={String(summary.reachedFinal)} />
            <Stat label="Jackpots won" value={String(summary.jackpotsWon)} />
            <Stat label="Split" value={pct(summary.splitRate)} />
            <Stat label="Steal" value={pct(summary.stealRate)} />
            <Stat label="Sting" value={pct(summary.stingRate)} />
            <Stat label="Honesty" value={pct(summary.honestyRate)} />
            <Stat label="Kept their word" value={pct(summary.intentMatchRate)} />
            <Stat label="Stolen from" value={String(summary.timesStolenFrom)} />
            <Stat
              label="Stings"
              value={`${summary.stingsRight} right / ${summary.stingsWrong} wrong`}
            />
            <Stat
              label="Avg round survived"
              value={summary.averageRoundSurvived?.toFixed(2) ?? '—'}
            />
            <Stat label="Biggest win" value={fmtMoney(summary.largestJackpot)} />
            <Stat label="Total won" value={fmtMoney(summary.totalWon)} />
            <Stat label="Timeouts" value={String(summary.timeoutCount)} />
            <Stat label="Abandoned" value={String(summary.abandonCount)} />
          </dl>
          <p className="font-serif text-[0.65rem] text-ec-bone-dim">
            Play money only — no real value, no purchase, no cash out. Matchmaking tiers:{' '}
            {data.tierFormula.text}
          </p>
        </>
      )}

      {data.historyVisible && data.history && data.history.length > 0 && (
        <details className="border-t border-ec-line pt-2">
          <summary className="cursor-pointer font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
            Per-game history ({data.history.length})
          </summary>
          <ul className="mt-2 flex flex-col gap-1">
            {data.history.map((row) => (
              <li
                key={row.matchId}
                className="flex flex-wrap items-baseline gap-x-2 border-t border-ec-line/40 pt-1 font-serif text-[0.65rem] first:border-0"
              >
                <span className="text-ec-bone-dim">
                  {new Date(row.playedAt).toLocaleDateString()}
                </span>
                <span className="text-ec-bone">
                  {row.reachedFinal ? `final · ${row.finalChoice ?? '—'}` : `out round ${row.eliminatedRound}`}
                </span>
                {row.isPrivateLobby && (
                  <span className="text-ec-bone-dim">private</span>
                )}
                <span className="ml-auto tabular-nums text-ec-brass">{fmtMoney(row.payout)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {!data.historyVisible && summary && summary.gamesPlayed > 0 && (
        <p className="font-serif text-[0.65rem] text-ec-bone-dim">
          Per-game history is private — summary rates only.
        </p>
      )}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="font-display text-[0.55rem] uppercase tracking-wide text-ec-bone-dim">
        {label}
      </dt>
      <dd className="font-serif text-sm tabular-nums text-ec-face">{value}</dd>
    </div>
  );
}
